import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { unwrapResult } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";

// Ability
import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";

// Others
import icons from "enums/icons";
import { urlPaths } from "enums/urlPaths";
import { capitalize, isEmpty } from "lodash";
import { ChartRouteNames } from "enums/chartEnums";

// Components
import {
    Box,
    Button,
    Chip,
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Skeleton,
    Link,
    Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import ProText from "components/Pro/ProText";
import CustomIcon from "components/CustomIcon";
import { HeaderStep } from "components/HeaderStep";
import { ErrorBoundary } from "react-error-boundary";
import GenericError from "components/Errors/GenericError";
import { Header, Body } from "components/Layout/LayoutContent";
import { CompanySummaryHeader } from "components/CompanySummary";
import SummaryCard, { SummaryCardRow } from "./components/SummaryCard/SummaryCard";
import CompanyInfoCard from "components/CompanyInfoCard";
import TitleFilter from "components/Talent/Companies/TitleFilter";
import NavButton from "components/HeaderNavigation/NavButton";
import Modal from "components/Modal";
import { useSnackBar, SnackBar } from "components/SnackBar";
import { FilterFieldButton, BriefCard } from "./components";
import ExpandableCardRow from "./components/ExpandableCardRow/ExpandableCardRow";
import EntityList, { RowHead, Row } from "./components/EntityList/EntityList";
import { CircledElement, SquaredButton, VerticalLine } from "./styled-components";
import MessageUpgrade from "components/Pro/Messages/MessageUpgrade";
import { HeaderInner } from "components/Layout/LayoutContent";
import CRMExport from "components/Team/CRMExport";
import { CreateProjectSectionModal } from "components/CreateProjectSectionModal";

// Adapters
import {
    getCompaniesList,
    getPeopleList,
    getSearchAIList,
    getBriefList,
    getInsightsList,
    SnackBarEntityTypes,
    getProjectCompany,
    convertProjectCompany,
    TConvertedCompaniesList,
    TConvertedPeopleList,
    convertProjectPerson,
    convertCompanyData,
    getProjectBriefsChildren,
    getProjectInsightsChildren,
    getProjectSearchAIsChildren,
    projectChunksToProjectData,
} from "./adapters/formattedData";

// Store
import { toggleLoader } from "store/slice/UI";
import { clearDomain } from "store/slice/Domain";
import { clearChart } from "store/slice/charts/chartsSlice";
import {
    resetBriefData,
    getProject,
    fetchProjectById,
    getProjectCompanyData,
    fetchProjectCompany,
} from "store/slice/Project/projectData/ProjectSlice";
import { clearLongList, clearRefinementData, clearShortList } from "store/slice/refinement";
import { clearBoardStructure, clearMightBeList } from "store/slice/Team";
import { addInsightsData, getInsights } from "store/slice/Project/insights/insightsSlice";
import { addBriefsData, getBriefs } from "store/slice/Project/briefs/briefsSlice";
import { addSearchAisData, getSearchAis } from "store/slice/Project/searchAis/searchAisSlice";
import { resetAdvancedPeers } from "store/slice/advancedPeersSearch";
import { getCompanySets } from "store/slice/Project/companiesSet/companiesSetSlice";
import { getPeopleSets } from "store/slice/Project/peopleSet/peopleSetSlice";

// Images
import createYourInsight from "assets/images/svgs/complete-insights.svg";
import createYourBrief from "assets/images/svgs/create-your-brief.svg";
import createYourSearch from "assets/images/svgs/start-your-search.svg";
import pathToImprovement from "assets/images/svgs/path-to-improvement.svg";

// Hooks
import { useMount } from "ahooks";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useAppziWidget, useAuth, useRedirectOnProjectError, useRequestSet } from "utils/hooks";
import useCreateProjectSectionModal from "components/CreateProjectSectionModal/useCreateProjectSectionModal";

// Types
import {
    BriefChildren,
    InsightData,
    ProjectDataAvatarHistory,
    ProjectDataResponse,
    ProjectSearchAICreationResponse,
} from "services/projects/project.types";

const showInfoInsightInitial: {
    id: number;
    clickedOn: "Path" | "Brief" | "";
} = {
    id: 0,
    clickedOn: "",
};

const entityInitialState = { searchAiId: 0, type: undefined, data: [] };

const ProjectDashboard = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // Redirect to 404 if invalid project id or not enough permission
    useRedirectOnProjectError();

    useMount(() => {
        dispatch(clearDomain());
        dispatch(resetAdvancedPeers());
        dispatch(resetBriefData());
        dispatch(clearBoardStructure());
        dispatch(clearLongList());
        dispatch(clearShortList());
        dispatch(clearRefinementData());
        dispatch(clearMightBeList());
    });

    const ability = useAbility(AbilityContext);

    const canCreateInsights = ability.can("see", "SAAS_LD_PROJECT_INSIGHT_CREATE");
    const canCreateBriefs = ability.can("see", "SAAS_LD_PROJECT_BRIEF_CREATE") || !ability.can("see", "LD_FREE");
    const canCreateSearchAI = ability.can("see", "SAAS_LD_PROJECT_SEARCH_AI_CREATE") || !ability.can("see", "LD_FREE");

    const { projectId } = useParams();
    const { loggedInUserData } = useAuth();
    const { handleOpenWidget } = useAppziWidget();
    const { requestSets, getCompanySetPath, getPersonSetPath } = useRequestSet();

    const { data: projectData } = useAppSelector(getProject);
    const { data: projectCompanyData } = useAppSelector(getProjectCompanyData);
    const companyDataForSummaryCard = convertCompanyData(projectCompanyData);
    const { shouldOpenSnackBar, popOverAnchorEl, handleOpenSnackBar, handleCloseSnackBar } = useSnackBar();

    const projectChunks = {
        data: useAppSelector(getProject).data,
        insights: useAppSelector(getInsights).data,
        briefs: useAppSelector(getBriefs).data,
        searchAIs: useAppSelector(getSearchAis).data,
        companySets: useAppSelector(getCompanySets).data,
        personSets: useAppSelector(getPeopleSets).data,
    };

    const [isNameInvalid, setIsNameInvalid] = useState(true);
    const [currentParentId, setCurrentParentId] = useState(0);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [insightsFilterValue, setInsightsFilterValue] = useState("");
    const [briefsFilterValue, setBriefsFilterValue] = useState("");
    const [searchAIFilterValue, setSearchAIFilterValue] = useState("");

    const [showInfoForInsight, setShowInfoForInsight] = useState<typeof showInfoInsightInitial>(showInfoInsightInitial);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [companyList, setCompanyList] = useState<ReturnType<typeof getCompaniesList>>(
        getCompaniesList(projectData) || []
    );
    const [peopleList, setPeopleList] = useState<ReturnType<typeof getPeopleList>>(getPeopleList(projectData) || []);
    const [insightsList, setInsightsList] = useState<ReturnType<typeof getInsightsList>>(
        getInsightsList(projectData) || []
    );
    const [briefList, setBriefList] = useState<ReturnType<typeof getBriefList>>(getBriefList(projectData) || []);
    const [searchAIList, setSearchAIList] = useState<ReturnType<typeof getSearchAIList>>(
        getSearchAIList(projectData) || []
    );
    const [selectedEntity, setSelectedEntity] = useState<{
        searchAiId: number;
        type: keyof typeof SnackBarEntityTypes | undefined;
        data: (TConvertedPeopleList | TConvertedCompaniesList)[];
    }>(entityInitialState);

    const [openCRMExport, setOpenCRMExport] = useState(false);
    const [selectedSearchAiId, setSelectedSearchAiId] = useState<number | null>(null);

    const wrapperRef = useRef<HTMLElement | null | undefined>(null);

    const companyId = projectCompanyData?.id;
    const headerSteps = useMemo(
        () =>
            [
                { label: "Complete your insights", shouldRender: true, shouldBeEnabled: true },
                { label: "Create your brief", shouldRender: true, shouldBeEnabled: canCreateBriefs },
                {
                    label: "Start your search",
                    shouldRender: canCreateSearchAI,
                    shouldBeEnabled: true,
                },
            ].filter((e) => e.shouldRender),
        [canCreateSearchAI, canCreateBriefs]
    );

    const handleCallBackAfterSaving = async (
        createdSectionData: InsightData | BriefChildren | ProjectSearchAICreationResponse
    ) => {
        if (showModalFor === "Insight") {
            const insightId = createdSectionData?.data.id;

            if (insightId) {
                dispatch(clearDomain());
                dispatch(toggleLoader(false));
                navigate(`${urlPaths.Domain}/${companyId}?projectId=${projectId}&insightId=${insightId}`, {
                    state: {
                        from: `${urlPaths.ProjectDashboard}/${projectId}`,
                    },
                });
            }
        }

        if (showModalFor === "Brief") {
            const briefId = createdSectionData?.data.id;

            if (briefId) {
                dispatch(clearChart());
                dispatch(resetBriefData());
                dispatch(toggleLoader(false));
                navigate(
                    `${urlPaths.Brief}/${companyId}?projectId=${projectId}&insightId=${currentParentId}&briefId=${briefId}`,
                    {
                        state: {
                            from: `${urlPaths.ProjectDashboard}/${projectId}`,
                        },
                    }
                );
            }
        }

        if (showModalFor === "SearchAi") {
            const searchAiId = createdSectionData?.data.id;
            const insightId = briefList.find((brief) => brief.id === currentParentId)?.meta.relatedInsight?.id;

            if (searchAiId && insightId) {
                dispatch(toggleLoader(false));
                navigate(
                    `${urlPaths.Talent}/${companyId}?projectId=${projectId}&insightId=${insightId}&searchAiId=${searchAiId}`,
                    {
                        state: {
                            from: `${urlPaths.ProjectDashboard}/${projectId}`,
                        },
                    }
                );
            }
        }
    };

    const {
        showModalFor,
        newSectioName,
        handleShowCreateProjectSectionModal,
        handleCreateProjectSection,
        handleUpdateSectionName,
        handleCloseProjectSectionCreateModal,
    } = useCreateProjectSectionModal(projectId, currentParentId, handleCallBackAfterSaving);

    const getShowInfoForInsightData = (insightId: number) => insightsList?.find((insight) => insight.id === insightId);

    const getInsightMissingPortion = (insightId: number) => {
        const insightData = getShowInfoForInsightData(insightId);
        const urlSearchParams = `${insightData?.meta.companyId}?projectId=${projectId}&insightId=${insightData?.id}`;

        if (!insightData?.meta?.peers) {
            return {
                url: `${urlPaths.Domain}/${urlSearchParams}`,
                label: "Complete Set Domain",
            };
        }

        if (!insightData?.meta?.slt) {
            return {
                url: `${urlPaths.Team}/${urlSearchParams}`,
                label: "Complete Set Team",
            };
        }

        if (!insightData?.meta?.vcp) {
            return {
                url: `${urlPaths.Vcp}/${urlSearchParams}`,
                label: "Complete VCP",
            };
        }

        return {
            url: `${urlPaths.ProjectDashboard}/${projectId}`,
            label: "Go to Dashboard",
        };
    };

    const handleClickSearchAICount = async (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        searchAI: any,
        type: keyof typeof SnackBarEntityTypes
    ) => {
        handleOpenSnackBar(event);
        setSelectedEntity((prevValue) => ({
            ...prevValue,
            searchAiId: searchAI.id,
            type,
        }));

        if (type === "company") {
            const path = getCompanySetPath(projectData.data.id, searchAI.meta.relatedCompanyListId);
            const response = await requestSets(projectData.data.id, [path], type);
            const list: TConvertedCompaniesList[] = response.map((c: any) => convertProjectCompany(c.data));
            setSelectedEntity((prevVal) => ({ ...prevVal, data: list }));
        }

        if (type === "people") {
            const path = getPersonSetPath(projectData.data.id, searchAI.meta.relatedLongListId);
            const response = await requestSets(projectData.data.id, [path], type);
            const list: TConvertedPeopleList[] = response.map((p: any) => convertProjectPerson(p.data));
            setSelectedEntity((prevVal) => ({ ...prevVal, data: list }));
        }

        if (type === "shortlist") {
            const path = getPersonSetPath(projectData.data.id, searchAI.meta.relatedShortListId);
            const response = await requestSets(projectData.data.id, [path], "people");
            const list: TConvertedPeopleList[] = response.map((p: any) => convertProjectPerson(p.data));
            setSelectedEntity((prevVal) => ({ ...prevVal, data: list }));
        }
    };

    // Try to make this works using generics
    // const filteredSectionList = (list: typeof insightsList | typeof briefList | typeof searchAIList, value: string) => {
    //     if (value === "") {
    //         return list;
    //     }
    //     return list.filter((item) => item.name.toLowerCase().includes(value.toLowerCase()));
    // };

    const insightsFilteredList = insightsList.filter((item) =>
        item.name.toLowerCase().includes(insightsFilterValue.toLowerCase())
    );
    const briefsFilteredList = briefList.filter((item) =>
        item.name.toLowerCase().includes(briefsFilterValue.toLowerCase())
    );
    const searchAIFilteredList = searchAIList.filter((item) =>
        item.name.toLowerCase().includes(searchAIFilterValue.toLowerCase())
    );

    const handleOpenModal = async () => {
        if (isEmpty(projectCompanyData)) return;
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleCreate = async (type: "Insight" | "Brief" | "SearchAi") => {
        handleShowCreateProjectSectionModal(type);
    };

    const handleHighlightRelatedWork = (id: number, type: "searchAi" | "brief" | "insight" | "company" | "people") => {
        if (type === "searchAi") {
            const selectedSearchAI = searchAIList.find((s) => s.id === id);
            const relatedBriefId = selectedSearchAI?.meta?.relatedBrief?.id;

            // Insight
            const selectedBrief = briefList.find((b) => b.id === relatedBriefId);
            const relatedInsightId = selectedBrief?.meta.relatedInsight?.id;

            // Brief
            const selectedBriefsIds = briefList
                .filter((s) => s.meta.relatedInsight?.id === relatedInsightId)
                .map((s) => s.id);

            // SearchAIs
            const selectedSearchAis = searchAIList.filter(
                (s) => s.meta?.relatedBrief?.id && selectedBriefsIds.includes(s.meta.relatedBrief.id)
            );

            // Company and People
            const selectedInsight = insightsList.find((i) => i.id === relatedInsightId);

            const relatedCompanyIds = [
                selectedInsight?.meta.peers,
                ...selectedSearchAis.map((s) => s?.meta.relatedCompanyListId),
            ];
            const relatedPeopleIds = [
                selectedInsight?.meta.slt,
                ...selectedSearchAis.flatMap((s) => [s?.meta.relatedLongListId, s?.meta.relatedShortListId]),
            ];

            const shouldToggle = !selectedSearchAI?.shouldHighlight;

            setSearchAIList((current) =>
                current.map((s) => ({
                    ...s,
                    shouldHide:
                        shouldToggle &&
                        !Boolean(s.meta?.relatedBrief?.id && selectedBriefsIds.includes(s.meta.relatedBrief.id)),
                    shouldHighlight: Boolean(
                        s.meta?.relatedBrief?.id && selectedBriefsIds.includes(s.meta.relatedBrief.id) && shouldToggle
                    ),
                }))
            );

            setBriefList((current) =>
                current.map((b) => ({
                    ...b,
                    shouldHide: shouldToggle && !selectedBriefsIds.includes(b.id),
                    shouldHighlight: selectedBriefsIds.includes(b.id) && shouldToggle,
                }))
            );

            setInsightsList((current) =>
                current.map((i) => ({
                    ...i,
                    shouldHide: shouldToggle && i.id !== relatedInsightId,
                    shouldHighlight: i.id === relatedInsightId && shouldToggle,
                }))
            );

            setCompanyList((current) =>
                current?.map((c) => ({
                    ...c,
                    shouldHide: shouldToggle && !relatedCompanyIds.includes(c.id),
                    shouldHighlight: shouldToggle && relatedCompanyIds.includes(c.id),
                }))
            );

            setPeopleList((current) =>
                current.map((p) => ({
                    ...p,
                    shouldHide: shouldToggle && !relatedPeopleIds.includes(p.id),
                    shouldHighlight: shouldToggle && relatedPeopleIds.includes(p.id),
                }))
            );
        }

        if (type === "brief") {
            // Insight
            const selectedBrief = briefList.find((b) => b.id === id);
            const relatedInsightId = selectedBrief?.meta.relatedInsight?.id;
            const selectedInsight = insightsList.find((i) => i.id === relatedInsightId);

            // Brief
            const relatedBriefIds = briefList
                .filter((b) => b.meta?.relatedInsight?.id === relatedInsightId)
                .map((b) => b.id);

            // SearchAi
            const selectedSearchAi = searchAIList.filter(
                (s) => s.meta?.relatedBrief?.id && relatedBriefIds.includes(s.meta.relatedBrief.id)
            );
            const selectedSearchAiIds = selectedSearchAi.map((s) => s.id);

            // Company and People
            const relatedCompanyIds = [
                selectedInsight?.meta.peers,
                ...selectedSearchAi.map((s) => s?.meta.relatedCompanyListId),
            ];
            const relatedPeopleIds = [
                selectedInsight?.meta.slt,
                ...selectedSearchAi.flatMap((s) => [s?.meta.relatedLongListId, s?.meta.relatedShortListId]),
            ];

            const shouldToggle = !selectedBrief?.shouldHighlight;

            setSearchAIList((current) =>
                current.map((s) => ({
                    ...s,
                    shouldHide: shouldToggle && !selectedSearchAiIds.includes(s.id),
                    shouldHighlight: selectedSearchAiIds.includes(s.id) && shouldToggle,
                }))
            );

            setBriefList((current) =>
                current.map((b) => ({
                    ...b,
                    shouldHide: shouldToggle && !relatedBriefIds.includes(b.id),
                    shouldHighlight: relatedBriefIds.includes(b.id) && shouldToggle,
                }))
            );

            setInsightsList((current) =>
                current.map((i) => ({
                    ...i,
                    shouldHide: shouldToggle && i.id !== relatedInsightId,
                    shouldHighlight: i.id === relatedInsightId && shouldToggle,
                }))
            );

            setCompanyList((current) =>
                current?.map((c) => ({
                    ...c,
                    shouldHide: shouldToggle && !relatedCompanyIds.includes(c.id),
                    shouldHighlight: shouldToggle && relatedCompanyIds.includes(c.id),
                }))
            );

            setPeopleList((current) =>
                current.map((p) => ({
                    ...p,
                    shouldHide: shouldToggle && !relatedPeopleIds.includes(p.id),
                    shouldHighlight: shouldToggle && relatedPeopleIds.includes(p.id),
                }))
            );
        }

        if (type === "insight") {
            // Insight
            const selectedInsight = insightsList.find((i) => i.id === id);

            // Brief
            const selectedBriefsIds = briefList.filter((s) => s.meta.relatedInsight?.id === id).map((s) => s.id);

            // SearchAi
            const selectedSearchAi = searchAIList.filter(
                (s) => s.meta?.relatedBrief && selectedBriefsIds.includes(s.meta.relatedBrief.id)
            );
            const selectedSearchAiIds = selectedSearchAi.map((s) => s.id);

            // Company and People
            const relatedCompanyIds = [
                selectedInsight?.meta.peers,
                ...selectedSearchAi.map((s) => s?.meta.relatedCompanyListId),
            ];
            const relatedPeopleIds = [
                selectedInsight?.meta.slt,
                ...selectedSearchAi.flatMap((s) => [s?.meta.relatedLongListId, s?.meta.relatedShortListId]),
            ];

            const shouldToggle = !selectedInsight?.shouldHighlight;

            setSearchAIList((current) =>
                current.map((s) => ({
                    ...s,
                    shouldHide: shouldToggle && !selectedSearchAiIds.includes(s.id),
                    shouldHighlight: selectedSearchAiIds.includes(s.id) && shouldToggle,
                }))
            );

            setBriefList((current) =>
                current.map((b) => ({
                    ...b,
                    shouldHide: shouldToggle && !selectedBriefsIds.includes(b.id),
                    shouldHighlight: selectedBriefsIds.includes(b.id) && shouldToggle,
                }))
            );

            setInsightsList((current) =>
                current.map((i) => ({
                    ...i,
                    shouldHide: shouldToggle && i.id !== id,
                    shouldHighlight: i.id === id && shouldToggle,
                }))
            );

            setCompanyList((current) =>
                current?.map((c) => ({
                    ...c,
                    shouldHide: shouldToggle && !relatedCompanyIds.includes(c.id),
                    shouldHighlight: shouldToggle && relatedCompanyIds.includes(c.id),
                }))
            );

            setPeopleList((current) =>
                current.map((p) => ({
                    ...p,
                    shouldHide: shouldToggle && !relatedPeopleIds.includes(p.id),
                    shouldHighlight: shouldToggle && relatedPeopleIds.includes(p.id),
                }))
            );
        }

        if (type === "company" || type === "people") {
            const selectedList =
                type === "company" ? companyList?.find((i) => i.id === id) : peopleList?.find((i) => i.id === id);

            // SearchAI
            const searchAI = searchAIList.find(
                (s) =>
                    selectedList &&
                    (s.meta.relatedCompanyListId === selectedList?.id ||
                        s.meta.relatedLongListId === selectedList?.id ||
                        s.meta.relatedShortListId === selectedList?.id)
            );

            // The if part should be exaclty the same as if the id belongs to a searchAi
            if (searchAI) {
                const selectedSearchAI = searchAIList.find((s) => s.id === searchAI?.id);
                const relatedBriefId = selectedSearchAI?.meta?.relatedBrief?.id;

                // Insight
                const selectedBrief = briefList.find((b) => b.id === relatedBriefId);
                const relatedInsightId = selectedBrief?.meta.relatedInsight?.id;

                // Brief
                const selectedBriefsIds = briefList
                    .filter((s) => s.meta.relatedInsight?.id === relatedInsightId)
                    .map((s) => s.id);

                // SearchAIs
                const selectedSearchAis = searchAIList.filter(
                    (s) => s.meta?.relatedBrief?.id && selectedBriefsIds.includes(s.meta.relatedBrief.id)
                );

                // Company and People
                const selectedInsight = insightsList.find((i) => i.id === relatedInsightId);

                const relatedCompanyIds = [
                    selectedInsight?.meta.peers,
                    ...selectedSearchAis.map((s) => s?.meta.relatedCompanyListId),
                ];
                const relatedPeopleIds = [
                    selectedInsight?.meta.slt,
                    ...selectedSearchAis.flatMap((s) => [s?.meta.relatedLongListId, s?.meta.relatedShortListId]),
                ];

                const shouldToggle = !selectedSearchAI?.shouldHighlight;

                setSearchAIList((current) =>
                    current.map((s) => ({
                        ...s,
                        shouldHide:
                            shouldToggle &&
                            !Boolean(s.meta?.relatedBrief?.id && selectedBriefsIds.includes(s.meta.relatedBrief.id)),
                        shouldHighlight: Boolean(
                            s.meta?.relatedBrief?.id &&
                                selectedBriefsIds.includes(s.meta.relatedBrief.id) &&
                                shouldToggle
                        ),
                    }))
                );

                setBriefList((current) =>
                    current.map((b) => ({
                        ...b,
                        shouldHide: shouldToggle && !selectedBriefsIds.includes(b.id),
                        shouldHighlight: selectedBriefsIds.includes(b.id) && shouldToggle,
                    }))
                );

                setInsightsList((current) =>
                    current.map((i) => ({
                        ...i,
                        shouldHide: shouldToggle && i.id !== relatedInsightId,
                        shouldHighlight: i.id === relatedInsightId && shouldToggle,
                    }))
                );

                setCompanyList((current) =>
                    current?.map((c) => ({
                        ...c,
                        shouldHide: shouldToggle && !relatedCompanyIds.includes(c.id),
                        shouldHighlight: shouldToggle && relatedCompanyIds.includes(c.id),
                    }))
                );

                setPeopleList((current) =>
                    current.map((p) => ({
                        ...p,
                        shouldHide: shouldToggle && !relatedPeopleIds.includes(p.id),
                        shouldHighlight: shouldToggle && relatedPeopleIds.includes(p.id),
                    }))
                );
            } else {
                // Insight
                const selectedInsights = insightsList.filter(
                    (i) => selectedList && i.meta[type === "company" ? "peers" : "slt"] === selectedList.id
                );
                const selectedInsightsIds = insightsList
                    .filter((i) => selectedList && i.meta[type === "company" ? "peers" : "slt"] === selectedList.id)
                    .map((i) => i.id);

                // Briefs
                const selectedBriefsIds = briefList
                    .filter((b) => b.meta.relatedInsight && selectedInsightsIds.includes(b.meta.relatedInsight.id))
                    .map((b) => b.id);

                // Search Ais
                const selectedSearchAis = searchAIList.filter(
                    (s) => s.meta.relatedBrief && selectedBriefsIds.includes(s.meta.relatedBrief.id)
                );
                const selectedSearchAiIds = selectedSearchAis.map((s) => s.id);

                // Company and People
                const relatedCompanyIds = [
                    ...selectedInsights.map((i) => i.meta.peers),
                    ...selectedSearchAis.map((s) => s?.meta.relatedCompanyListId),
                ];
                const relatedPeopleIds = [
                    ...selectedInsights.map((i) => i.meta.slt),
                    ...selectedSearchAis.flatMap((s) => [s?.meta.relatedLongListId, s?.meta.relatedShortListId]),
                ];

                const shouldToggle = !selectedList?.shouldHighlight;

                setCompanyList((current) =>
                    current?.map((c) => ({
                        ...c,
                        shouldHide: shouldToggle && !relatedCompanyIds.includes(c.id),
                        shouldHighlight: shouldToggle && relatedCompanyIds.includes(c.id),
                    }))
                );

                setPeopleList((current) =>
                    current.map((p) => ({
                        ...p,
                        shouldHide: shouldToggle && !relatedPeopleIds.includes(p.id),
                        shouldHighlight: shouldToggle && relatedPeopleIds.includes(p.id),
                    }))
                );

                setInsightsList((current) =>
                    current.map((s) => ({
                        ...s,
                        shouldHide: shouldToggle && !selectedInsightsIds.includes(s.id),
                        shouldHighlight: selectedInsightsIds.includes(s.id) && shouldToggle,
                    }))
                );

                setBriefList((current) =>
                    current.map((s) => ({
                        ...s,
                        shouldHide: shouldToggle && !selectedBriefsIds.includes(s.id),
                        shouldHighlight: selectedBriefsIds.includes(s.id) && shouldToggle,
                    }))
                );

                setSearchAIList((current) =>
                    current.map((s) => ({
                        ...s,
                        shouldHide: shouldToggle && !selectedSearchAiIds.includes(s.id),
                        shouldHighlight: selectedSearchAiIds.includes(s.id) && shouldToggle,
                    }))
                );
            }
        }
    };

    const handleHighlightMyWork = (isChecked: boolean) => {
        const getMyLogsCount = (activityLog: ProjectDataAvatarHistory[] | undefined) =>
            Boolean(activityLog?.filter((log) => log.id === loggedInUserData.userId).length);

        setCompanyList((current) =>
            current?.map((c) => ({
                ...c,
                shouldHighlight: false,
                shouldHide: isChecked && !getMyLogsCount(c.activityLog),
            }))
        );

        setPeopleList((current) =>
            current.map((p) => ({
                ...p,
                shouldHighlight: false,
                shouldHide: isChecked && !getMyLogsCount(p.activityLog),
            }))
        );

        setInsightsList((current) =>
            current.map((i) => ({
                ...i,
                shouldHighlight: false,
                shouldHide: isChecked && !getMyLogsCount(i.activityLog),
            }))
        );

        setBriefList((current) =>
            current.map((b) => ({
                ...b,
                shouldHighlight: false,
                shouldHide: isChecked && !getMyLogsCount(b.activityLog),
            }))
        );

        setSearchAIList((current) =>
            current.map((s) => ({
                ...s,
                shouldHighlight: false,
                shouldHide: isChecked && !getMyLogsCount(s.activityLog),
            }))
        );
    };

    // Check creation name while typing
    useEffect(() => {
        const handleIsNameInvalid = (list: any) => {
            return Boolean(list.find((element: any) => element?.name.toLowerCase() === newSectioName.toLowerCase()));
        };

        if (!newSectioName || !showModalFor) return;

        if (showModalFor?.includes("Insight")) {
            setIsNameInvalid(handleIsNameInvalid(insightsList));
        }

        if (showModalFor?.includes("Brief")) {
            setIsNameInvalid(handleIsNameInvalid(briefList));
        }

        if (showModalFor?.includes("SearchAi")) {
            setIsNameInvalid(handleIsNameInvalid(searchAIList));
        }
    }, [briefList, insightsList, searchAIList, newSectioName, showModalFor]);

    // Initial Fetch
    useEffect(() => {
        const initialFetch = async () => {
            let projectResponse = {} as ProjectDataResponse;
            setIsLoadingData(true);

            if (isEmpty(projectData) && projectId) {
                try {
                    const data = await dispatch(fetchProjectById({ projectId: Number(projectId) }));
                    projectResponse = unwrapResult(data);
                } catch (error) {
                    console.error(error);
                    setIsLoadingData(false);
                }
            }

            const companyId = getProjectCompany(projectResponse)?.data.id;

            if (isEmpty(projectCompanyData) && companyId) {
                try {
                    await dispatch(fetchProjectCompany(companyId));
                } catch (error) {
                    console.error(error);
                    setIsLoadingData(false);
                }
            }

            const data = !isEmpty(projectResponse)
                ? projectResponse
                : projectChunksToProjectData(projectData, projectChunks);
            setData(data);
            setIsLoadingData(false);
        };

        const setData = (data: ProjectDataResponse) => {
            setCompanyList(getCompaniesList(data) || []);
            setPeopleList(getPeopleList(data) || []);
            setInsightsList(getInsightsList(data) || []);
            setBriefList(getBriefList(data) || []);
            setSearchAIList(getSearchAIList(data) || []);

            dispatch(addInsightsData(getProjectInsightsChildren(data)));
            dispatch(addBriefsData(getProjectBriefsChildren(data)));
            dispatch(addSearchAisData(getProjectSearchAIsChildren(data)));
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCRMExport = (id: number) => {
        setSelectedSearchAiId(id);
        setOpenCRMExport(true);
    };

    return (
        <ErrorBoundary FallbackComponent={GenericError}>
            <Header>
                <HeaderInner>
                    {isLoadingData ? (
                        <Skeleton variant="rounded" width={250} height={42} />
                    ) : (
                        <CompanySummaryHeader title={projectCompanyData?.name} />
                    )}
                    {!isLoadingData && (
                        <Box display="flex" alignItems="center">
                            {headerSteps.map((el, i) => {
                                return (
                                    <HeaderStep
                                        key={i}
                                        disabled={!el.shouldBeEnabled}
                                        innerLabel={
                                            el.shouldBeEnabled ? (
                                                `${i + 1}`
                                            ) : (
                                                <CustomIcon icon={icons.lock} sx={{ fontSize: 18 }} />
                                            )
                                        }
                                        label={el.label}
                                        shouldShowProText={!el.shouldBeEnabled}
                                        sx={{ marginRight: i < headerSteps.length ? 3 : 0 }}
                                    />
                                );
                            })}
                        </Box>
                    )}
                </HeaderInner>
            </Header>
            <Body>
                {/* Summary / Companies and People lists */}
                <Box sx={{ p: 2, pb: 3, boxShadow: "1px 0px 7px 0px #00000040" }}>
                    <Grid container spacing={3}>
                        <Grid item lg={4}>
                            {isLoadingData || companyDataForSummaryCard === undefined ? (
                                <Skeleton variant="rounded" width="100%" height={164} />
                            ) : (
                                <SummaryCard>
                                    <SummaryCardRow config={companyDataForSummaryCard.products} />
                                    <SummaryCardRow config={companyDataForSummaryCard.sectors} />
                                    <SummaryCardRow config={companyDataForSummaryCard.capitalStructure} />
                                    <SummaryCardRow
                                        config={{
                                            name: companyDataForSummaryCard?.transactions?.name,
                                            icon: companyDataForSummaryCard?.transactions?.icon,
                                            items: [],
                                        }}
                                        onOpenModal={handleOpenModal}
                                    />
                                </SummaryCard>
                            )}
                        </Grid>
                        <Grid item lg={4}>
                            {isLoadingData ? (
                                <Skeleton variant="rounded" width="100%" height={164} />
                            ) : (
                                <EntityList
                                    title="People Lists"
                                    dataType="people"
                                    data={peopleList}
                                    onClickRelatedButton={handleHighlightRelatedWork}
                                />
                            )}
                        </Grid>
                        <Grid item lg={4}>
                            {isLoadingData ? (
                                <Skeleton variant="rounded" width="100%" height={164} />
                            ) : (
                                <EntityList
                                    title="Company Lists"
                                    dataType="company"
                                    data={companyList}
                                    onClickRelatedButton={handleHighlightRelatedWork}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Box>

                {/* My Work */}
                <Grid container spacing={1} sx={{ mt: 0.5, px: 2, position: "relative", zIndex: 2 }}>
                    <Grid item lg={12} display="flex" alignItems="center" justifyContent="flex-end">
                        {isLoadingData ? (
                            <Skeleton variant="rounded" width={124} height={38} />
                        ) : (
                            <FormControlLabel
                                label={<Typography sx={{ fontSize: 16 }}>My Work</Typography>}
                                control={
                                    <Switch
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                            handleHighlightMyWork(event.target.checked)
                                        }
                                    />
                                }
                            />
                        )}
                    </Grid>
                </Grid>

                <Box sx={{ position: "relative" }}>
                    {/* Insights */}
                    <Grid container spacing={3} sx={{ px: 2 }}>
                        <Grid item lg={12} display="flex">
                            <Box justifyContent="center">
                                <CircledElement size={34} sx={{ mt: 0.85 }}>
                                    1
                                </CircledElement>
                                <VerticalLine />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ mb: 1 }}>
                                    <Box display="flex" alignItems="center">
                                        <TitleFilter title="Insights" icon={<CustomIcon icon={icons.sparkles} />} />
                                        {!isLoadingData && (
                                            <Button
                                                variant="outlined"
                                                sx={{ height: 36 }}
                                                onClick={() => handleCreate("Insight")}
                                            >
                                                + <FormattedMessage id="project.dashboard.new.insight" />
                                            </Button>
                                        )}
                                    </Box>
                                    {!isLoadingData && (
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Box>
                                                <Typography variant="body1">
                                                    <FormattedMessage id="dashboard.insights.legend" />
                                                </Typography>
                                            </Box>
                                            <Box display="flex">
                                                {insightsList.length ? (
                                                    <FilterFieldButton
                                                        value={insightsFilterValue}
                                                        setValue={setInsightsFilterValue}
                                                        placeholder="Filter Insights"
                                                    />
                                                ) : (
                                                    <></>
                                                )}
                                                <CircledElement size={34} color="info" sx={{ ml: 1 }}>
                                                    {insightsList.length}
                                                </CircledElement>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                                <Box>
                                    <Box
                                        sx={{
                                            backgroundColor: "background.paleBlue",
                                            width: "100%",
                                            p: 2,
                                            pr: 0,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                pr: 2,
                                                maxHeight: insightsList.length ? 260 : "initial",
                                                overflowY: insightsList.length ? "scroll" : "visible",
                                            }}
                                        >
                                            {/* Loader */}
                                            {isLoadingData && (
                                                <>
                                                    <Skeleton
                                                        variant="rounded"
                                                        width="100%"
                                                        height={64}
                                                        sx={{ mb: 1 }}
                                                    />
                                                    <Skeleton variant="rounded" width="100%" height={64} />
                                                </>
                                            )}

                                            {/* No data placeholder */}
                                            {!isLoadingData && !insightsList.length && canCreateInsights && (
                                                <Grid
                                                    container
                                                    sx={{
                                                        maxWidth: 1170,
                                                        margin: "auto",
                                                        flexWrap: "nowrap",
                                                        px: 2,
                                                        py: 4,
                                                        justifyContent: "space-between",
                                                    }}
                                                >
                                                    <Grid
                                                        item
                                                        sx={{
                                                            backgroundColor: "common.white",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                            padding: 3,
                                                            mx: 1,
                                                            borderRadius: 1,
                                                            boxShadow:
                                                                "0px 2px 3px 0px #0000000D, 0px 6px 9px 0px #0000001A",
                                                        }}
                                                    >
                                                        <Box display="flex" alignItems="center" mb={2}>
                                                            <CircledElement size={34}>1</CircledElement>
                                                            <Typography sx={{ ml: 2 }}>
                                                                Complete Your Insights
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            sx={{ display: "flex", flex: 1, justifyContent: "center" }}
                                                        >
                                                            <img
                                                                src={createYourInsight}
                                                                style={{ maxWidth: "90%" }}
                                                                alt=""
                                                            />
                                                        </Box>
                                                    </Grid>

                                                    <Grid
                                                        item
                                                        sx={{
                                                            backgroundColor: "common.white",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                            padding: 3,
                                                            mx: 1,
                                                            borderRadius: 1,
                                                            boxShadow:
                                                                "0px 2px 3px 0px #0000000D, 0px 6px 9px 0px #0000001A",
                                                        }}
                                                    >
                                                        <Box display="flex" alignItems="center" mb={2}>
                                                            <CircledElement size={34}>2</CircledElement>
                                                            <Typography sx={{ ml: 2 }}>Complete Your Brief</Typography>
                                                        </Box>
                                                        <Box
                                                            sx={{ display: "flex", flex: 1, justifyContent: "center" }}
                                                        >
                                                            <img
                                                                src={createYourBrief}
                                                                style={{ maxWidth: "90%" }}
                                                                alt=""
                                                            />
                                                        </Box>
                                                    </Grid>

                                                    <Grid
                                                        item
                                                        sx={{
                                                            backgroundColor: "common.white",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                            padding: 3,
                                                            mx: 1,
                                                            borderRadius: 1,
                                                            boxShadow:
                                                                "0px 2px 3px 0px #0000000D, 0px 6px 9px 0px #0000001A",
                                                        }}
                                                    >
                                                        <Box display="flex" alignItems="center" mb={2}>
                                                            <CircledElement size={34}>3</CircledElement>
                                                            <Typography sx={{ ml: 2 }}>Start Your Search</Typography>
                                                        </Box>
                                                        <Box
                                                            sx={{ display: "flex", flex: 1, justifyContent: "center" }}
                                                        >
                                                            <img
                                                                src={createYourSearch}
                                                                style={{ maxWidth: "90%" }}
                                                                alt=""
                                                            />
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            )}

                                            {/* Has Data */}
                                            {!isLoadingData &&
                                                insightsFilteredList.length > 0 &&
                                                insightsFilteredList.map((insight) => {
                                                    return (
                                                        <Box
                                                            key={insight.id}
                                                            sx={{
                                                                display: insight.shouldHide ? "none" : "flex",
                                                                alignItems: "center",
                                                                marginBottom: 1,
                                                            }}
                                                        >
                                                            <ExpandableCardRow
                                                                type="insight"
                                                                onClickRelatedButton={handleHighlightRelatedWork}
                                                                isDisabled={insight.isDisabled}
                                                                data={insight}
                                                                setShowInfoForInsight={setShowInfoForInsight}
                                                                options={
                                                                    <>
                                                                        <Box
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            m="auto"
                                                                        >
                                                                            <Link
                                                                                component="button"
                                                                                onClick={() =>
                                                                                    navigate(
                                                                                        `${urlPaths.Domain}/${insight.meta.companyId}?projectId=${projectId}&insightId=${insight.id}`,
                                                                                        {
                                                                                            state: {
                                                                                                from: `${urlPaths.ProjectDashboard}/${projectId}`,
                                                                                            },
                                                                                        }
                                                                                    )
                                                                                }
                                                                                sx={(theme) => ({
                                                                                    display: "inline-block",
                                                                                    fontSize: 11,
                                                                                    margin: "0 4px 0 auto",
                                                                                    mr: 0.5,
                                                                                    letterSpacing: 1,
                                                                                    color: theme.palette.secondary.main,
                                                                                })}
                                                                            >
                                                                                Set Domain
                                                                            </Link>
                                                                            {!Boolean(insight.meta.peers) && (
                                                                                <Tooltip
                                                                                    title="Complete setting your domain to progress."
                                                                                    placement="bottom-end"
                                                                                    arrow
                                                                                >
                                                                                    <Box
                                                                                        display="flex"
                                                                                        alignItems="center"
                                                                                    >
                                                                                        <CustomIcon
                                                                                            icon={icons.errorTriangle}
                                                                                            sx={{
                                                                                                fontSize: 14,
                                                                                                color: "error.main",
                                                                                            }}
                                                                                        />
                                                                                    </Box>
                                                                                </Tooltip>
                                                                            )}
                                                                        </Box>

                                                                        <Box
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            m="auto"
                                                                        >
                                                                            <Link
                                                                                component="button"
                                                                                disabled={!Boolean(insight.meta.peers)}
                                                                                onClick={() =>
                                                                                    navigate(
                                                                                        `${urlPaths.Team}/${insight.meta.companyId}?projectId=${projectId}&insightId=${insight.id}`,
                                                                                        {
                                                                                            state: {
                                                                                                from: `${urlPaths.ProjectDashboard}/${projectId}`,
                                                                                            },
                                                                                        }
                                                                                    )
                                                                                }
                                                                                sx={(theme) => ({
                                                                                    display: "inline-block",
                                                                                    mr: 0.5,
                                                                                    fontSize: 11,
                                                                                    letterSpacing: 1,
                                                                                    color: theme.palette.secondary.main,
                                                                                    "&:disabled": {
                                                                                        color: theme.palette.text
                                                                                            .disabled,
                                                                                        cursor: "default",
                                                                                        opacity: 0.75,
                                                                                    },
                                                                                })}
                                                                            >
                                                                                Set Team
                                                                            </Link>
                                                                            {Boolean(insight.meta.peers) &&
                                                                                (!Boolean(insight.meta.slt) ||
                                                                                    insight.meta.sltCount === 0) && (
                                                                                    <Tooltip
                                                                                        title="Complete setting your team to progress."
                                                                                        placement="bottom-end"
                                                                                        arrow
                                                                                    >
                                                                                        <span>
                                                                                            <CustomIcon
                                                                                                icon={
                                                                                                    icons.errorTriangle
                                                                                                }
                                                                                                sx={{
                                                                                                    fontSize: 14,
                                                                                                    color: "error.main",
                                                                                                }}
                                                                                            />
                                                                                        </span>
                                                                                    </Tooltip>
                                                                                )}
                                                                        </Box>

                                                                        <Box
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            m="auto"
                                                                        >
                                                                            <Link
                                                                                component="button"
                                                                                disabled={
                                                                                    !Boolean(insight.meta.slt) ||
                                                                                    insight.meta.sltCount === 0
                                                                                }
                                                                                onClick={() =>
                                                                                    navigate(
                                                                                        `${urlPaths.Vcp}/${insight.meta.companyId}?projectId=${projectId}&insightId=${insight.id}`,
                                                                                        {
                                                                                            state: {
                                                                                                from: `${urlPaths.ProjectDashboard}/${projectId}`,
                                                                                            },
                                                                                        }
                                                                                    )
                                                                                }
                                                                                sx={(theme) => ({
                                                                                    display: "inline-block",
                                                                                    mr: 0.5,
                                                                                    fontSize: 11,
                                                                                    letterSpacing: 1,
                                                                                    color: theme.palette.secondary.main,
                                                                                    "&:disabled": {
                                                                                        color: theme.palette.text
                                                                                            .disabled,
                                                                                        cursor: "default",
                                                                                        opacity: 0.75,
                                                                                    },
                                                                                })}
                                                                            >
                                                                                Set VCP
                                                                            </Link>
                                                                            {Boolean(
                                                                                insight.meta.slt &&
                                                                                    insight.meta.sltCount &&
                                                                                    insight.meta.peers &&
                                                                                    !insight.meta.vcp
                                                                            ) && (
                                                                                <Tooltip
                                                                                    title="Complete setting your vcp to progress."
                                                                                    placement="bottom-end"
                                                                                    arrow
                                                                                >
                                                                                    <Box
                                                                                        display="flex"
                                                                                        alignItems="center"
                                                                                    >
                                                                                        <CustomIcon
                                                                                            icon={icons.errorTriangle}
                                                                                            sx={{
                                                                                                fontSize: 14,
                                                                                                color: "error.main",
                                                                                            }}
                                                                                        />
                                                                                    </Box>
                                                                                </Tooltip>
                                                                            )}
                                                                        </Box>

                                                                        <Link
                                                                            component="button"
                                                                            disabled={
                                                                                !Boolean(
                                                                                    insight.meta.peers &&
                                                                                        insight.meta.slt &&
                                                                                        insight.meta.sltCount &&
                                                                                        insight.meta.vcp
                                                                                )
                                                                            }
                                                                            onClick={() => {
                                                                                dispatch(clearChart());
                                                                                navigate(
                                                                                    `${urlPaths.Projection}/${insight.meta.companyId}?projectId=${projectId}&insightId=${insight.id}`
                                                                                );
                                                                            }}
                                                                            sx={(theme) => ({
                                                                                display: "inline-block",
                                                                                margin: "auto",
                                                                                fontSize: 11,
                                                                                letterSpacing: 1,
                                                                                color: theme.palette.secondary.main,
                                                                                "&:disabled": {
                                                                                    color: theme.palette.text.disabled,
                                                                                    cursor: "default",
                                                                                    opacity: 0.75,
                                                                                },
                                                                            })}
                                                                        >
                                                                            View Insight
                                                                        </Link>
                                                                    </>
                                                                }
                                                            />
                                                            {!insight.isDisabled ? (
                                                                <Box display="flex" alignItems="stretch">
                                                                    <SquaredButton
                                                                        disabled={
                                                                            !insight.meta.slt ||
                                                                            insight.meta.sltCount === 0 ||
                                                                            !insight.meta.vcp
                                                                        }
                                                                        onClick={() => {
                                                                            if (
                                                                                !insight.meta.slt ||
                                                                                insight.meta.sltCount === 0 ||
                                                                                !insight.meta.vcp
                                                                            ) {
                                                                                setShowInfoForInsight({
                                                                                    id: insight.id,
                                                                                    clickedOn: "Path",
                                                                                });
                                                                            } else {
                                                                                dispatch(clearChart());
                                                                                navigate(
                                                                                    `${urlPaths.Projection}/${insight.meta.companyId}/${ChartRouteNames.Bridge}?projectId=${projectId}&insightId=${insight.id}`
                                                                                );
                                                                            }
                                                                        }}
                                                                    >
                                                                        <img
                                                                            data-showonhover
                                                                            src={pathToImprovement}
                                                                            alt=""
                                                                        />
                                                                        <Box data-hideonhover>
                                                                            <Typography
                                                                                sx={{ fontSize: 9, letterSpacing: 0.4 }}
                                                                            >
                                                                                Path to
                                                                                <br />
                                                                                Improvement
                                                                            </Typography>
                                                                        </Box>
                                                                    </SquaredButton>
                                                                    <SquaredButton
                                                                        color="secondary"
                                                                        disabled={
                                                                            !insight.meta.slt ||
                                                                            !insight.meta.sltCount ||
                                                                            !insight.meta.vcp
                                                                        }
                                                                        onClick={() => {
                                                                            if (!canCreateBriefs) {
                                                                                handleOpenWidget();
                                                                                return;
                                                                            }

                                                                            if (
                                                                                !insight.meta.slt ||
                                                                                !insight.meta.sltCount ||
                                                                                !insight.meta.vcp
                                                                            ) {
                                                                                setShowInfoForInsight({
                                                                                    id: insight.id,
                                                                                    clickedOn: "Brief",
                                                                                });
                                                                            } else {
                                                                                setCurrentParentId(insight.id);
                                                                                handleCreate("Brief");
                                                                            }
                                                                        }}
                                                                    >
                                                                        {!canCreateBriefs && (
                                                                            <CustomIcon
                                                                                data-showonhover
                                                                                sx={{ fontSize: 32 }}
                                                                                icon={icons.documentLightBulb}
                                                                            />
                                                                        )}
                                                                        <Box
                                                                            {...(!canCreateBriefs
                                                                                ? { "data-hideonhover": true }
                                                                                : {})}
                                                                        >
                                                                            {!canCreateBriefs && (
                                                                                <ProText
                                                                                    color="white"
                                                                                    sx={{ mb: 0.5 }}
                                                                                />
                                                                            )}
                                                                            <Typography
                                                                                sx={{ fontSize: 9, letterSpacing: 0.4 }}
                                                                            >
                                                                                Create
                                                                                <br />
                                                                                Brief
                                                                            </Typography>
                                                                        </Box>
                                                                    </SquaredButton>
                                                                </Box>
                                                            ) : (
                                                                <></>
                                                            )}
                                                        </Box>
                                                    );
                                                })}

                                            {/* No Results from API and no permissions */}
                                            {!isLoadingData &&
                                                !canCreateInsights &&
                                                !insightsFilteredList.length &&
                                                !insightsFilterValue && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            height: 170,
                                                        }}
                                                    >
                                                        <Box display="flex" alignItems="center">
                                                            <CustomIcon icon={icons.sparkles} />
                                                            <Typography ml={0.5} variant="subtitle2">
                                                                Create your first Insight to start your search.
                                                            </Typography>
                                                        </Box>
                                                        <Button
                                                            color="primary"
                                                            variant="contained"
                                                            sx={{ mt: 1, height: 36 }}
                                                            onClick={() => handleCreate("Insight")}
                                                        >
                                                            + <FormattedMessage id="project.dashboard.new.insight" />
                                                        </Button>
                                                    </Box>
                                                )}

                                            {/* No Results from filter */}
                                            {!isLoadingData && !insightsFilteredList.length && insightsFilterValue && (
                                                <Box sx={{ display: "flex", height: 170 }}>
                                                    <Typography variant="subtitle2" sx={{ margin: "auto" }}>
                                                        <FormattedMessage id="dashboard.filters.noResults" />
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Briefs */}
                    <Grid container spacing={3} sx={{ mt: 1, px: 2 }}>
                        <Grid item lg={12} display="flex">
                            <Box>
                                <CircledElement size={34} sx={{ mt: 0.85 }}>
                                    2
                                </CircledElement>
                                {canCreateSearchAI && <VerticalLine />}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ mb: 1 }}>
                                    <TitleFilter
                                        title="Briefs"
                                        icon={<CustomIcon icon={icons.reportLightBulb} />}
                                        isDisabled={Boolean(!insightsList.length && !briefList.length)}
                                    />
                                    {!isLoadingData && canCreateBriefs && (insightsList.length || briefList.length) ? (
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Box>
                                                <Typography variant="body1">
                                                    <FormattedMessage id="dashboard.briefs.legend" />
                                                </Typography>
                                            </Box>

                                            <Box display="flex">
                                                {briefList.length ? (
                                                    <FilterFieldButton
                                                        value={briefsFilterValue}
                                                        setValue={setBriefsFilterValue}
                                                        placeholder="Filter Briefs"
                                                    />
                                                ) : (
                                                    <></>
                                                )}
                                                <CircledElement size={34} color="info" sx={{ ml: 1 }}>
                                                    {briefList.length}
                                                </CircledElement>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <></>
                                    )}
                                </Box>
                                <Box>
                                    <Box
                                        sx={{
                                            backgroundColor: "background.grey",
                                            width: "100%",
                                            padding: 2,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                maxHeight: canCreateBriefs && briefList.length ? 230 : "initial",
                                                overflowY: canCreateBriefs && briefList.length ? "scroll" : "visible",
                                                ...(!canCreateBriefs
                                                    ? {
                                                          display: "flex",
                                                          justifyContent: "center",
                                                      }
                                                    : {}),
                                            }}
                                        >
                                            {/* Loader */}
                                            {isLoadingData && (
                                                <>
                                                    <Skeleton
                                                        variant="rounded"
                                                        width="100%"
                                                        height={64}
                                                        sx={{ mb: 1 }}
                                                    />
                                                    <Skeleton variant="rounded" width="100%" height={64} />
                                                </>
                                            )}

                                            {/* No data placeholder */}
                                            {Boolean(
                                                canCreateBriefs &&
                                                    !isLoadingData &&
                                                    !insightsList.length &&
                                                    !briefList.length
                                            ) && (
                                                <Box
                                                    sx={{
                                                        p: 1,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <CustomIcon
                                                        icon={icons.lock}
                                                        sx={{ color: "#454852", fontSize: 20, mr: 1 }}
                                                    />
                                                    <Typography sx={{ color: "#454852" }}>
                                                        Complete your insights above to get started
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* No data, but briefs placeholder */}
                                            {canCreateBriefs &&
                                                !isLoadingData &&
                                                Boolean(insightsList.length) &&
                                                !Boolean(briefList.length) && (
                                                    <Box
                                                        sx={{
                                                            p: 1,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                                            <CustomIcon
                                                                icon={icons.sparkles}
                                                                sx={{ color: "#454852", fontSize: 24, mr: 1 }}
                                                            />
                                                            <CustomIcon
                                                                icon={icons.thinArrowRight}
                                                                sx={{ color: "#454852", fontSize: 26, mr: 1 }}
                                                            />
                                                            <CustomIcon
                                                                icon={icons.documentLightBulb}
                                                                sx={{ color: "#454852", fontSize: 20, mr: 1 }}
                                                            />
                                                        </Box>
                                                        <Typography sx={{ color: "#454852" }}>
                                                            Create a brief using completed insights to get started
                                                        </Typography>
                                                    </Box>
                                                )}

                                            {/* Has Data */}
                                            {canCreateBriefs && !isLoadingData && briefsFilteredList.length > 0 && (
                                                <Grid container spacing={3}>
                                                    {briefsFilteredList
                                                        .filter((brief) => brief.meta?.relatedInsight?.id)
                                                        .map((brief) => (
                                                            <Grid
                                                                item
                                                                lg={3}
                                                                key={brief.id}
                                                                sx={{
                                                                    display: brief.shouldHide ? "none" : "block",
                                                                }}
                                                            >
                                                                <BriefCard
                                                                    data={brief}
                                                                    companyId={companyId}
                                                                    onClickCreateSearchAi={() => {
                                                                        if (brief.id) {
                                                                            setCurrentParentId(brief.id);
                                                                            handleCreate("SearchAi");
                                                                        }
                                                                    }}
                                                                    onClickRelatedButton={handleHighlightRelatedWork}
                                                                />
                                                            </Grid>
                                                        ))}
                                                </Grid>
                                            )}

                                            {/* Pro Feature*/}
                                            {!isLoadingData && !canCreateBriefs && (
                                                <MessageUpgrade
                                                    width={455}
                                                    variant="type1"
                                                    icon={icons.proStar}
                                                    title={"pro.message.heading"}
                                                >
                                                    <Typography sx={{ fontSize: 16, margin: "12px 0 12px 0" }}>
                                                        Using insights of the leadership team created, our briefs will
                                                        help define your search criteria outlining a detailed leadership
                                                        profile and scope of the search.
                                                    </Typography>
                                                </MessageUpgrade>
                                            )}

                                            {/* No Results from filter */}
                                            {!isLoadingData && !briefsFilteredList.length && briefsFilterValue && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        height: 170,
                                                        backgroundColor: "background.grey",
                                                        margin: "auto",
                                                    }}
                                                >
                                                    <Typography variant="subtitle2" sx={{ margin: "auto" }}>
                                                        <FormattedMessage id="dashboard.filters.noResults" />
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Completed */}
                                        <Box>
                                            {canCreateBriefs &&
                                                !isLoadingData &&
                                                Boolean(briefList.filter((s) => s.isCompleted).length) && (
                                                    <Accordion
                                                        sx={{
                                                            background: "none",
                                                            boxShadow: "none",
                                                            "& .MuiButtonBase-root": {
                                                                padding: 0,
                                                                justifyContent: "flex-start",
                                                            },
                                                            "& .MuiAccordionSummary-content": { flex: 0 },
                                                            "& .MuiAccordionDetails-root": { padding: 0 },
                                                        }}
                                                    >
                                                        <AccordionSummary
                                                            expandIcon={
                                                                <CustomIcon
                                                                    icon={icons.chevronDownThin}
                                                                    sx={{ fontSize: 14 }}
                                                                />
                                                            }
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 15,
                                                                    letterSpacing: 1,
                                                                    mr: 0.6,
                                                                    whiteSpace: "pre",
                                                                }}
                                                            >
                                                                Completed Briefs
                                                            </Typography>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            <Grid container spacing={3}>
                                                                {briefList.map((brief) => (
                                                                    <Grid item lg={3} key={brief.id}>
                                                                        <BriefCard
                                                                            data={brief}
                                                                            companyId={companyId}
                                                                            onClickRelatedButton={
                                                                                handleHighlightRelatedWork
                                                                            }
                                                                        />
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* SearchAI */}
                    {canCreateSearchAI && (
                        <Grid container spacing={3} sx={{ mt: 1, px: 2 }}>
                            <Grid item lg={12} display="flex">
                                <Box>
                                    <CircledElement size={34} sx={{ mt: 0.85 }}>
                                        3
                                    </CircledElement>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ mb: 1 }}>
                                        <TitleFilter
                                            title="SearchAI"
                                            icon={
                                                <CustomIcon
                                                    icon={icons.flashLight}
                                                    sx={{
                                                        fontSize: 28,
                                                        opacity: Boolean(!briefList.length && !searchAIList.length)
                                                            ? 0.5
                                                            : 1,
                                                    }}
                                                />
                                            }
                                            isDisabled={Boolean(!briefList.length && !searchAIList.length)}
                                        />
                                        {Boolean(
                                            !isLoadingData &&
                                                canCreateSearchAI &&
                                                (insightsList.length || briefList.length || searchAIList.length)
                                        ) && (
                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        opacity: Boolean(!briefList?.length && !searchAIList?.length)
                                                            ? 0.5
                                                            : 1,
                                                    }}
                                                >
                                                    <FormattedMessage id="dashboard.searcAI.legend" />
                                                </Typography>
                                                <Box display="flex">
                                                    {searchAIList.length ? (
                                                        <FilterFieldButton
                                                            value={searchAIFilterValue}
                                                            setValue={setSearchAIFilterValue}
                                                            placeholder="Filter Searches"
                                                        />
                                                    ) : (
                                                        <></>
                                                    )}
                                                    <CircledElement size={34} color="info" sx={{ ml: 1 }}>
                                                        {searchAIList.length}
                                                    </CircledElement>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>

                                    <Box
                                        sx={{
                                            backgroundColor: "background.grey",
                                            width: "100%",
                                            padding: 2,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                pr: 2,
                                                maxHeight: canCreateSearchAI && searchAIList.length ? 260 : "initial",
                                                overflowY:
                                                    canCreateSearchAI && searchAIList.length ? "scroll" : "visible",
                                                ...(!canCreateSearchAI
                                                    ? {
                                                          display: "flex",
                                                          justifyContent: "center",
                                                      }
                                                    : {}),
                                            }}
                                        >
                                            {/* Loader */}
                                            {isLoadingData && (
                                                <>
                                                    <Skeleton
                                                        variant="rounded"
                                                        width="100%"
                                                        height={64}
                                                        sx={{ mb: 1 }}
                                                    />
                                                    <Skeleton variant="rounded" width="100%" height={64} />
                                                </>
                                            )}

                                            {/* No data placeholder */}
                                            {Boolean(
                                                canCreateSearchAI &&
                                                    !isLoadingData &&
                                                    !briefList.length &&
                                                    !searchAIList.length
                                            ) && (
                                                <Box
                                                    sx={{
                                                        p: 1,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <CustomIcon
                                                        icon={icons.lock}
                                                        sx={{ color: "#454852", fontSize: 20, mr: 1 }}
                                                    />
                                                    <Typography sx={{ color: "#454852" }}>
                                                        <FormattedMessage id="dashboard.searchAI.completeInsights" />
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* No data, but briefs placeholder */}
                                            {Boolean(
                                                canCreateSearchAI &&
                                                    !isLoadingData &&
                                                    briefList.length &&
                                                    !searchAIList.length
                                            ) && (
                                                <Box
                                                    sx={{
                                                        p: 1,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                                        <CustomIcon
                                                            icon={icons.documentLightBulb}
                                                            sx={{ color: "#454852", fontSize: 20, mr: 1 }}
                                                        />
                                                        <CustomIcon
                                                            icon={icons.thinArrowRight}
                                                            sx={{ color: "#454852", fontSize: 26, mr: 1 }}
                                                        />
                                                        <CustomIcon
                                                            icon={icons.flashLight}
                                                            sx={{ color: "#454852", fontSize: 24, mr: 1 }}
                                                        />
                                                    </Box>
                                                    <Typography sx={{ color: "#454852" }}>
                                                        <FormattedMessage id="dashboard.searchAI.startSearch" />
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Has Data */}
                                            {canCreateSearchAI &&
                                                !isLoadingData &&
                                                searchAIFilteredList.length > 0 &&
                                                searchAIFilteredList.map((searchAI) => (
                                                    <Box
                                                        key={searchAI.id}
                                                        sx={{
                                                            display: searchAI.shouldHide ? "none" : "flex",
                                                            alignItems: "center",
                                                            marginBottom: 1,
                                                        }}
                                                    >
                                                        <ExpandableCardRow
                                                            type="searchAi"
                                                            data={searchAI}
                                                            onClickRelatedButton={handleHighlightRelatedWork}
                                                            options={
                                                                <>
                                                                    <Chip
                                                                        sx={{ mr: 0.5 }}
                                                                        size="small"
                                                                        variant="filled"
                                                                        label="Company"
                                                                        disabled={isEmpty(searchAI.meta.relatedBrief)}
                                                                        onDelete={(ev) =>
                                                                            handleClickSearchAICount(
                                                                                ev,
                                                                                searchAI,
                                                                                SnackBarEntityTypes.company
                                                                            )
                                                                        }
                                                                        deleteIcon={
                                                                            <Box display="flex" alignItems="center">
                                                                                <CircledElement size={16} color="info">
                                                                                    {searchAI.meta.companies_count}
                                                                                </CircledElement>
                                                                            </Box>
                                                                        }
                                                                        onClick={() => {
                                                                            dispatch(clearChart());
                                                                            navigate(
                                                                                `${urlPaths.Talent}/${companyId}?projectId=${searchAI.meta.projectId}&insightId=${searchAI.meta.relatedInsightId}&briefId=${searchAI.meta.relatedBriefId}&searchAiId=${searchAI.meta.pathId}`,
                                                                                {
                                                                                    state: {
                                                                                        from: `${urlPaths.ProjectDashboard}/${projectId}`,
                                                                                    },
                                                                                }
                                                                            );
                                                                        }}
                                                                    />
                                                                    <Chip
                                                                        sx={{ mr: 0.5 }}
                                                                        size="small"
                                                                        variant="filled"
                                                                        label="People"
                                                                        disabled={
                                                                            isEmpty(searchAI.meta.relatedBrief) ||
                                                                            !searchAI.meta.relatedCompanyListId
                                                                        }
                                                                        onDelete={(ev) =>
                                                                            handleClickSearchAICount(
                                                                                ev,
                                                                                searchAI,
                                                                                SnackBarEntityTypes.people
                                                                            )
                                                                        }
                                                                        deleteIcon={
                                                                            <Box display="flex" alignItems="center">
                                                                                <CircledElement size={16} color="info">
                                                                                    {searchAI.meta.people_count}
                                                                                </CircledElement>
                                                                            </Box>
                                                                        }
                                                                        onClick={() => {
                                                                            dispatch(clearChart());
                                                                            navigate(
                                                                                `${urlPaths.TalentPeople}/${companyId}?projectId=${searchAI.meta.projectId}&insightId=${searchAI.meta.relatedInsightId}&briefId=${searchAI.meta.relatedBriefId}&searchAiId=${searchAI.meta.pathId}`,
                                                                                {
                                                                                    state: {
                                                                                        from: `${urlPaths.ProjectDashboard}/${projectId}`,
                                                                                    },
                                                                                }
                                                                            );
                                                                        }}
                                                                    />
                                                                    <Chip
                                                                        sx={{ mr: 0.5 }}
                                                                        size="small"
                                                                        variant="filled"
                                                                        label="Shortlist"
                                                                        disabled={
                                                                            isEmpty(searchAI.meta.relatedBrief) ||
                                                                            !searchAI.meta.relatedLongListId ||
                                                                            !searchAI.meta.relatedShortListId
                                                                        }
                                                                        onDelete={(ev) =>
                                                                            handleClickSearchAICount(
                                                                                ev,
                                                                                searchAI,
                                                                                SnackBarEntityTypes.shortlist
                                                                            )
                                                                        }
                                                                        deleteIcon={
                                                                            <Box display="flex" alignItems="center">
                                                                                <CircledElement size={16} color="info">
                                                                                    {searchAI.meta.shortlist_count}
                                                                                </CircledElement>
                                                                            </Box>
                                                                        }
                                                                        onClick={() => {
                                                                            dispatch(clearChart());
                                                                            navigate(
                                                                                `${urlPaths.TalentRefinement}/${companyId}?projectId=${searchAI.meta.projectId}&insightId=${searchAI.meta.relatedInsightId}&briefId=${searchAI.meta.relatedBriefId}&searchAiId=${searchAI.meta.pathId}`,
                                                                                {
                                                                                    state: {
                                                                                        from: `${urlPaths.ProjectDashboard}/${projectId}`,
                                                                                    },
                                                                                }
                                                                            );
                                                                        }}
                                                                    />
                                                                </>
                                                            }
                                                        />

                                                        <Box display="flex" alignItems="stretch">
                                                            <SquaredButton
                                                                sx={{ flexDirection: "column" }}
                                                                onClick={() => handleCRMExport(searchAI.id)}
                                                                disabled={!searchAI.meta.shortlist_count}
                                                            >
                                                                <CustomIcon icon={icons.share} sx={{ fontSize: 16 }} />
                                                                <Typography sx={{ fontSize: 9, fontWeight: 600 }}>
                                                                    Export to
                                                                    <br />
                                                                    CRM
                                                                </Typography>
                                                            </SquaredButton>
                                                        </Box>
                                                    </Box>
                                                ))}

                                            {openCRMExport && (
                                                <CRMExport
                                                    open={openCRMExport}
                                                    onClose={() => setOpenCRMExport(false)}
                                                    searchAiId={selectedSearchAiId}
                                                />
                                            )}

                                            {/* No Results from filter */}
                                            {!isLoadingData && !searchAIFilteredList.length && searchAIFilterValue && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        height: 170,
                                                        backgroundColor: "background.grey",
                                                        margin: "auto",
                                                    }}
                                                >
                                                    <Typography variant="subtitle2" sx={{ margin: "auto" }}>
                                                        <FormattedMessage id="dashboard.filters.noResults" />
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Completed */}
                                        {Boolean(
                                            canCreateSearchAI &&
                                                !isLoadingData &&
                                                searchAIList.filter((s) => s.isCompleted).length
                                        ) && (
                                            <Box>
                                                <Accordion
                                                    sx={{
                                                        background: "none",
                                                        boxShadow: "none",
                                                        "& .MuiButtonBase-root": {
                                                            padding: 0,
                                                            justifyContent: "flex-start",
                                                        },
                                                        "& .MuiAccordionSummary-content": { flex: 0 },
                                                        "& .MuiAccordionDetails-root": { padding: 0 },
                                                    }}
                                                >
                                                    <AccordionSummary
                                                        expandIcon={
                                                            <CustomIcon
                                                                icon={icons.chevronDownThin}
                                                                sx={{ fontSize: 14 }}
                                                            />
                                                        }
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: 15,
                                                                letterSpacing: 1,
                                                                mr: 0.6,
                                                                whiteSpace: "pre",
                                                            }}
                                                        >
                                                            Completed Searches
                                                        </Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        {searchAIList.map((searchAI) => (
                                                            <Box
                                                                key={searchAI.id}
                                                                sx={{
                                                                    display: searchAI.shouldHide ? "none" : "flex",
                                                                    marginBottom: 1,
                                                                }}
                                                            >
                                                                <ExpandableCardRow
                                                                    type="searchAi"
                                                                    onClickRelatedButton={handleHighlightRelatedWork}
                                                                    isCompleted
                                                                    data={searchAI}
                                                                    options={
                                                                        <>
                                                                            <Chip
                                                                                sx={{
                                                                                    mr: 0.5,
                                                                                    borderColor: "primary.dark",
                                                                                }}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                label="Company"
                                                                                onDelete={() => void 0}
                                                                                deleteIcon={
                                                                                    <Box
                                                                                        display="flex"
                                                                                        alignItems="center"
                                                                                    >
                                                                                        <CircledElement
                                                                                            size={16}
                                                                                            color="dark"
                                                                                        >
                                                                                            2
                                                                                        </CircledElement>
                                                                                    </Box>
                                                                                }
                                                                                onClick={() => console.log("click")}
                                                                            />
                                                                            <Chip
                                                                                sx={{
                                                                                    mr: 0.5,
                                                                                    borderColor: "primary.dark",
                                                                                }}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                label="People"
                                                                                onDelete={() => void 0}
                                                                                deleteIcon={
                                                                                    <Box
                                                                                        display="flex"
                                                                                        alignItems="center"
                                                                                    >
                                                                                        <CircledElement
                                                                                            size={16}
                                                                                            color="dark"
                                                                                        >
                                                                                            3
                                                                                        </CircledElement>
                                                                                    </Box>
                                                                                }
                                                                                onClick={() => console.log("click")}
                                                                            />
                                                                            <Chip
                                                                                sx={{
                                                                                    mr: 0.5,
                                                                                    borderColor: "primary.dark",
                                                                                }}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                label="Shortlist"
                                                                                onDelete={() => void 0}
                                                                                deleteIcon={
                                                                                    <Box
                                                                                        display="flex"
                                                                                        alignItems="center"
                                                                                    >
                                                                                        <CircledElement
                                                                                            size={16}
                                                                                            color="dark"
                                                                                        >
                                                                                            4
                                                                                        </CircledElement>
                                                                                    </Box>
                                                                                }
                                                                                onClick={() => console.log("click")}
                                                                            />
                                                                        </>
                                                                    }
                                                                />
                                                            </Box>
                                                        ))}
                                                    </AccordionDetails>
                                                </Accordion>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    )}

                    {/* Creation Modal */}
                    <CreateProjectSectionModal
                        modalFor={showModalFor}
                        isNameInvalid={isNameInvalid}
                        textInput={newSectioName}
                        textInputPlaceholder=""
                        handleTextInputChange={handleUpdateSectionName}
                        handleCreateSection={handleCreateProjectSection}
                        handleCloseModal={handleCloseProjectSectionCreateModal}
                    />

                    {/* SnackBar SearchAI */}
                    <SnackBar
                        maxWidth={wrapperRef.current?.getBoundingClientRect().width || 400}
                        shouldOpen={shouldOpenSnackBar}
                        anchorElement={popOverAnchorEl}
                        title={<strong>{capitalize(selectedEntity.type)}</strong>}
                        handleInfoClose={(ev) => {
                            handleCloseSnackBar(ev);
                            setSelectedEntity(entityInitialState);
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "center",
                        }}
                        {...(wrapperRef.current
                            ? {
                                  anchorPosition: {
                                      top: wrapperRef.current?.getBoundingClientRect().bottom + 5,
                                      left:
                                          wrapperRef.current?.getBoundingClientRect().left +
                                          wrapperRef.current?.getBoundingClientRect().width / 2,
                                  },
                              }
                            : {})}
                        content={
                            <>
                                <RowHead
                                    title={
                                        <FormattedMessage
                                            id={`project.dashboard.popup.header.${
                                                selectedEntity.type === SnackBarEntityTypes.company
                                                    ? "company"
                                                    : "people"
                                            }.subterm`}
                                        />
                                    }
                                />
                                <Box sx={{ px: 2, maxHeight: 300, overflowY: "scroll" }}>
                                    {selectedEntity.type && !Boolean(selectedEntity.data.length) && (
                                        <>
                                            <Skeleton
                                                variant="rectangular"
                                                animation="wave"
                                                height={28}
                                                sx={{ mb: 0.5 }}
                                            />
                                            <Skeleton
                                                variant="rectangular"
                                                animation="wave"
                                                height={28}
                                                sx={{ mb: 0.5 }}
                                            />
                                            <Skeleton
                                                variant="rectangular"
                                                animation="wave"
                                                height={28}
                                                sx={{ mb: 0.5 }}
                                            />
                                            <Skeleton
                                                variant="rectangular"
                                                animation="wave"
                                                height={28}
                                                sx={{ mb: 0.5 }}
                                            />
                                            <Skeleton variant="rectangular" animation="wave" height={28} />
                                        </>
                                    )}

                                    {selectedEntity.type === SnackBarEntityTypes.company &&
                                        (selectedEntity.data as TConvertedCompaniesList[])?.map((c) => (
                                            <Row
                                                key={c.id}
                                                term={c.name ?? ""}
                                                value={c.currentCapitalStructure ?? ""}
                                            />
                                        ))}

                                    {selectedEntity.type === SnackBarEntityTypes.people &&
                                        (selectedEntity.data as TConvertedPeopleList[])?.map((p) => (
                                            <Row
                                                key={p.id}
                                                term={p.name}
                                                subTerm={p.jobTitle}
                                                value={p.company ?? ""}
                                            />
                                        ))}

                                    {selectedEntity.type === SnackBarEntityTypes.shortlist &&
                                        (selectedEntity.data as TConvertedPeopleList[])?.map((p) => (
                                            <Row
                                                key={p.id}
                                                term={p.name}
                                                subTerm={p.jobTitle}
                                                value={p.company ?? ""}
                                            />
                                        ))}

                                    {/* {selectedEntity.type === SnackBarEntityTypes.company &&
                                        searchAIList
                                            .find((s) => s.id === selectedEntity.searchAiId)
                                            ?.companyData?.map((company) => (
                                                <Row
                                                    key={company.id}
                                                    term={company.name ?? ""}
                                                    value={company.currentCapitalStructure ?? ""}
                                                />
                                            ))}
                                    {selectedEntity.type === SnackBarEntityTypes.people &&
                                        searchAIList
                                            .find((s) => s.id === selectedEntity.searchAiId)
                                            ?.longListData?.map((p) => (
                                                <Row
                                                    key={p.id}
                                                    term={p.name}
                                                    subTerm={p.jobTitle}
                                                    value={p.company ?? ""}
                                                />
                                            ))}
                                    {selectedEntity.type === SnackBarEntityTypes.shortlist &&
                                        searchAIList
                                            .find((s) => s.id === selectedEntity.searchAiId)
                                            ?.shortListData?.map((p) => (
                                                <Row
                                                    key={p.id}
                                                    term={p.name}
                                                    subTerm={p.jobTitle}
                                                    value={p.company ?? ""}
                                                />
                                            ))} */}
                                </Box>
                            </>
                        }
                    />
                </Box>
            </Body>

            {/* CompanyInfo Modal */}
            <Modal open={isModalOpen}>
                <CompanyInfoCard onCloseModal={handleCloseModal} data={companyDataForSummaryCard} />
            </Modal>

            {/* Complete Insight Modal */}
            <Modal open={Boolean(showInfoForInsight.id)} onClose={() => setShowInfoForInsight(showInfoInsightInitial)}>
                <Box sx={{ width: "100%", maxWidth: 500, p: 2, borderRadius: 0.8, backgroundColor: "background.blue" }}>
                    <Box display="flex" alignItems="center">
                        <InfoIcon />
                        <Typography sx={{ fontSize: 18, ml: 1 }}>Incomplete Insight</Typography>
                    </Box>
                    <Box pt={1.2} pb={1}>
                        <Typography>
                            {showInfoForInsight.clickedOn === "Path"
                                ? "Complete your insight to progress to Path to Improvement and view your Leadership Bridge."
                                : "Complete your insight to Create Brief."}
                        </Typography>
                    </Box>
                    <Box display="flex" justifyContent="flex-end">
                        <Button onClick={() => setShowInfoForInsight(showInfoInsightInitial)}>Dismiss</Button>
                        <NavButton
                            onClick={() =>
                                navigate(getInsightMissingPortion(showInfoForInsight.id)?.url, {
                                    state: {
                                        from: `${urlPaths.ProjectDashboard}/${projectId}`,
                                    },
                                })
                            }
                        >{`${getInsightMissingPortion(showInfoForInsight.id)?.label}`}</NavButton>
                    </Box>
                </Box>
            </Modal>
        </ErrorBoundary>
    );
};

export default ProjectDashboard;
